package com.neurobs.util

import scala.scalajs.js
import scala.scalajs.js.annotation.JSGlobal
import scala.scalajs.js.{UndefOr, |}

//@ScalaJSDefined
class DataPoint
(
	val x: Double,
	val y: Double
) extends js.Object

object DataPoint {
	def apply(x: Double, y: Double) = new DataPoint(x = x, y = y)
}

//@ScalaJSDefined
class DataSet
(
	val label: String = "",
	val backgroundColor: String = "blue",
	val borderColor: String = "blue",
	val fill: Boolean = false,
	val pointRadius: Double = 3,
	val pointBackgroundColor: String = "black",
	val pointBorderColor: String = "black",
	val data: js.Array[Double] | js.Array[DataPoint]
) extends js.Object

//@ScalaJSDefined
class ChartData
(
	val labels: js.Array[String] = js.Array(),
	val datasets: js.Array[DataSet]
) extends js.Object

//@ScalaJSDefined
class ScaleLabelOptions
(
	val labelString: String,
	val display: Boolean = true
) extends js.Object

//@ScalaJSDefined
trait TickOptions extends js.Object {
	val min: UndefOr[Double] = js.undefined
}

//@ScalaJSDefined
class BarAxisOptions
(
	val scaleLabel: ScaleLabelOptions,
	val ticks: TickOptions = new TickOptions {}
) extends js.Object

//@ScalaJSDefined
class LineAxisOptions
(
	val `type`: String = "linear",
//	val position: String = "bottom",
	val scaleLabel: ScaleLabelOptions
) extends js.Object

//@ScalaJSDefined
class ScaleOptions
(
	val xAxes: js.Array[BarAxisOptions | LineAxisOptions] = js.Array(),
	val yAxes: js.Array[BarAxisOptions | LineAxisOptions] = js.Array()
) extends js.Object

/**
 * Options affecting the chart title
 *
 * @param display Is the title shown?
 * @param position Position of title
 * @param fontSize Font size
 * @param fontFamily Font family for the title text
 * @param fontColor Font color
 * @param fontStyle Font style
 * @param padding Number of pixels to add above and below the title text
 * @param lineHeight Height of an individual line of text. See MDN
 * @param text Title text to display. If specified as an array, text is rendered on multiple lines.
 */
//@ScalaJSDefined
class TitleOptions
(
	val display: Boolean = false,
	val position: String = TitleOptions.positionTop,
	val fontSize: Double = 12,
	val fontFamily: String = "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
	val fontColor: String = "#666",
	val fontStyle: String = "bold",
	val padding: Int = 10,
	val lineHeight: Double = 1.2,
	val text: String | js.Array[String] = ""
) extends js.Object

object TitleOptions {
	val positionTop = "top"
	val positionLeft = "left"
	val positionBottom = "bottom"
	val positionRight = "right"
}

//@ScalaJSDefined
class LegendOptions
(
	val display: Boolean = false
) extends js.Object

//@ScalaJSDefined
class AnimationOptions
(
	val duration: Double = 0
) extends js.Object

//@ScalaJSDefined
class HoverOptions
(
	val animationDuration: Double = 0
) extends js.Object

//@ScalaJSDefined
class ChartOptions
(
	val title: TitleOptions = new TitleOptions(),
	val legend: LegendOptions = new LegendOptions(),
	val scales: ScaleOptions = new ScaleOptions(),
	val animation: AnimationOptions = new AnimationOptions(),
	val hover: HoverOptions = new HoverOptions(),
	val responsiveAnimationDuration: Double = 0,
	val responsive: Boolean = true
) extends js.Object

//@ScalaJSDefined
class ChartParameters
(
	val `type`: String,
	val data: ChartData,
	val options: ChartOptions = new ChartOptions()
) extends js.Object

@js.native
@JSGlobal
class Chart (canvasContext: js.Dynamic, data: ChartParameters) extends js.Object {
	def update(): Unit = js.native
	def resize(): Unit = js.native
}